import React, { useState } from 'react';
import './BookNow.css';

/* 
...existing code...
Interested Package options are intentionally commented out for now.
Re-enable this block later when you're ready to bring packages back.
...existing code...
*/

export default function BookNow() {
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    phone_number: '',
    preferred_contact: '',
    event_type: '',
    guest_count: '',
    event_date: '',
    package: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) return value;

    const [, area, middle, last] = match;
    if (last) return `(${area}) ${middle}-${last}`;
    if (middle) return `(${area}) ${middle}`;
    if (area) return `(${area}`;
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'phone_number' ? formatPhoneNumber(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!isValidEmail(formData.user_email)) {
      alert('Please enter a valid email address.');
      return;
    }

    const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const apiCandidates = isLocalhost
      ? ['http://localhost:3001/send-email', '/send-email']
      : ['/send-email'];

    try {
       setIsSubmitting(true);
       let lastError = null;

       for (const apiUrl of apiCandidates) {
         try {
           const controller = new AbortController();
           const timeoutId = setTimeout(() => controller.abort(), 20000);

           const response = await fetch(apiUrl, {
             method: 'POST',
             headers: {
               'Content-Type': 'application/json',
             },
             body: JSON.stringify(formData),
             signal: controller.signal,
           });

           clearTimeout(timeoutId);

           if (!response.ok) {
             let serverMessage = '';
             try {
               const data = await response.json();
               serverMessage = data?.error || data?.detail || '';
             } catch (_) {
               // Ignore parsing failures and use status text fallback.
             }
             throw new Error(serverMessage || `Request failed (${response.status})`);
           }

           setSubmitted(true);
           setFormData({
             user_name: '',
             user_email: '',
             phone_number: '',
             preferred_contact: '',
             event_type: '',
             guest_count: '',
             event_date: '',
             package: '',
             message: ''
           });

           // Exit early once one endpoint succeeds.
           return;
         } catch (err) {
           lastError = err;
         }
       }

       throw lastError || new Error('Failed to send email');
     } catch (error) {
       console.error('Error sending email:', error);
       alert('Failed to send booking request. Please email contact@habibteachi.com directly with your booking details.');
     } finally {
       setIsSubmitting(false);
     }
  };

  return (
    <div className="container py-5">
      <h2 className="text-center display-5 fw-bold fade-in" style={{ color: '#406b60', animationDelay: '0.1s' }}>
        Book Us For Your Event
      </h2>

      {!submitted ? (
        <form
          className="booking-form p-4 shadow rounded bg-white fade-in"
          style={{ animationDelay: '0.1s' }}
          onSubmit={handleSubmit}
        >
          <div className="mb-3 fade-in" style={{ animationDelay: '0.2s' }}>
            <label className="form-label" style={{ color: '#406b60' }}>Name</label>
            <input
              type="text"
              name="user_name"
              className="form-control"
              required
              disabled={isSubmitting}
              value={formData.user_name}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3 fade-in" style={{ animationDelay: '0.3s' }}>
            <label className="form-label" style={{ color: '#406b60' }}>Email</label>
            <input
              type="text"
              name="user_email"
              className={`form-control ${formData.user_email && !isValidEmail(formData.user_email) ? 'is-invalid' : ''}`}
              required
              disabled={isSubmitting}
              value={formData.user_email}
              onChange={handleChange}
            />
            {formData.user_email && !isValidEmail(formData.user_email) && (
              <div className="invalid-feedback d-block">
              </div>
            )}
          </div>

          <div className="mb-3 fade-in" style={{ animationDelay: '0.32s' }}>
            <label className="form-label" style={{ color: '#406b60' }}>Phone Number</label>
            <input
              type="tel"
              name="phone_number"
              className="form-control"
              required
              disabled={isSubmitting}
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="(xxx) xxx-xxxx"
            />
          </div>

          <div className="mb-3 fade-in" style={{ animationDelay: '0.33s' }}>
            <label className="form-label" style={{ color: '#406b60' }}>Preferred Contact Method</label>
            <select
              name="preferred_contact"
              className="form-select"
              required
              disabled={isSubmitting}
              value={formData.preferred_contact}
              onChange={handleChange}
            >
              <option value="">Choose one...</option>
              <option value="Call">Call</option>
              <option value="Text">Text</option>
              <option value="Email">Email</option>
            </select>
          </div>

          <div className="mb-3 fade-in" style={{ animationDelay: '0.35s' }}>
            <label className="form-label" style={{ color: '#406b60' }}>Event Type</label>
            <select
              name="event_type"
              className="form-select"
              required
              disabled={isSubmitting}
              value={formData.event_type}
              onChange={handleChange}
            >
              <option value="">Choose one...</option>
              <option value="Wedding">Wedding</option>
              <option value="Birthday">Birthday</option>
              <option value="Corporate Event">Corporate Event</option>
              <option value="Pop-up">Pop-up</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/*
            ...existing code...
            Interested Package field removed for now.
            Re-add the dropdown and preview card here when you're ready.
            ...existing code...
          */}

          <div className="mb-3 fade-in" style={{ animationDelay: '0.4s' }}>
            <label className="form-label" style={{ color: '#406b60' }}>Number of Guests</label>
            <input
              type="number"
              name="guest_count"
              className="form-control"
              required
              disabled={isSubmitting}
              value={formData.guest_count}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3 fade-in" style={{ animationDelay: '0.45s' }}>
            <label className="form-label" style={{ color: '#406b60' }}>Event Date</label>
            <input
              type="date"
              name="event_date"
              className="form-control"
              required
              disabled={isSubmitting}
              value={formData.event_date}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3 fade-in" style={{ animationDelay: '0.5s' }}>
            <label className="form-label" style={{ color: '#406b60' }}>Message</label>
            <textarea
              name="message"
              className="form-control"
              rows="4"
              disabled={isSubmitting}
              value={formData.message}
              onChange={handleChange}
            />
          </div>

          <button
            className="btn btn-success w-100 fade-in submit-btn"
            style={{ animationDelay: '0.6s' }}
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Sending Request...
              </>
            ) : (
              'Submit Booking'
            )}
          </button>
        </form>
      ) : (
        <div className="alert alert-success text-center fade-in" style={{ animationDelay: '0.2s' }}>
          🎉 Thank you! Your booking request has been sent. We’ll reach out to confirm soon.
        </div>
      )}
    </div>
  );
}
